import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, Tv, Play, Clock, Calendar } from 'lucide-react'
import api from '../api/client'
import { videos } from '../api/client'
import { useAuthStore } from '../stores/auth'
import { useVideoPlayerStore } from '../stores/videoPlayer'
import VideoMenu from '../components/VideoMenu'

interface Episode {
  id: string
  title: string
  video_type: string
  year: number | null
  duration_ms: number | null
  file_path: string
  resolution: string | null
  codec: string | null
  container: string | null
  library_id: string
  series_id: string | null
  season_number: number | null
  episode_number: number | null
  overview: string | null
  thumbnail_path: string | null
}

interface SeriesInfo {
  id: string
  title: string
}

function formatDuration(ms: number | null): string {
  if (!ms) return ''
  const minutes = Math.round(ms / 60000)
  if (minutes < 60) return `${minutes} min`
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  return `${hours}h ${mins}m`
}

export default function EpisodePage() {
  const { seriesId, episodeId } = useParams<{ seriesId: string; episodeId: string }>()
  const token = useAuthStore((state) => state.token)
  const { playVideo, currentVideo, isPlaying } = useVideoPlayerStore()

  // Fetch episode details
  const { data: episode, isLoading: episodeLoading } = useQuery<Episode>({
    queryKey: ['episode', episodeId],
    queryFn: async () => {
      const response = await api.get(`/movies/${episodeId}`)
      return response.data
    },
    enabled: !!episodeId,
  })

  // Fetch series info for breadcrumb
  const { data: series } = useQuery<SeriesInfo>({
    queryKey: ['series-info', seriesId],
    queryFn: async () => {
      const response = await api.get(`/series/${seriesId}`)
      return { id: response.data.id, title: response.data.title }
    },
    enabled: !!seriesId,
  })

  // Fetch watched status
  const { data: watchedData } = useQuery({
    queryKey: ['video-watched', episodeId],
    queryFn: () => videos.getWatchedStatus(episodeId!),
    enabled: !!episodeId,
  })

  const isWatched = watchedData?.watched || false

  const getStreamUrl = () => {
    if (!episodeId) return ''
    if (import.meta.env.DEV) {
      const host = window.location.hostname
      return `http://${host}:4322/api/stream/video/${episodeId}/transcode?token=${token}`
    }
    return `/api/stream/video/${episodeId}/transcode?token=${token}`
  }

  const handlePlay = () => {
    if (!episode) return

    playVideo({
      id: episode.id,
      title: episode.title,
      seriesTitle: series?.title,
      seasonNumber: episode.season_number ?? undefined,
      episodeNumber: episode.episode_number ?? undefined,
      thumbnailPath: episode.thumbnail_path ?? undefined,
      durationMs: episode.duration_ms ?? undefined,
      streamUrl: getStreamUrl(),
    })
  }

  const handleWatchedChange = (_watched: boolean) => {
    // The VideoMenu component handles this internally
  }

  // Check if this episode is currently playing
  const isCurrentlyPlaying = currentVideo?.id === episodeId && isPlaying

  if (episodeLoading) {
    return (
      <div className="p-4 sm:p-6">
        <div className="text-dark-400">Loading...</div>
      </div>
    )
  }

  if (!episode) {
    return (
      <div className="p-4 sm:p-6">
        <div className="text-dark-400">Episode not found</div>
      </div>
    )
  }

  return (
    <div className="p-4 sm:p-6 max-w-4xl">
      {/* Back button */}
      <Link
        to={seriesId ? `/tv/${seriesId}` : '/tv'}
        className="inline-flex items-center gap-2 text-dark-400 hover:text-white mb-4 sm:mb-6 text-sm sm:text-base"
      >
        <ArrowLeft size={18} className="sm:w-5 sm:h-5" />
        {series ? `Back to ${series.title}` : 'Back to TV Shows'}
      </Link>

      {/* Episode Header */}
      <div className="mb-6">
        {/* Episode number badge */}
        {episode.season_number !== null && episode.episode_number !== null && (
          <p className="text-primary-400 text-sm font-medium mb-2">
            Season {episode.season_number} Episode {episode.episode_number}
          </p>
        )}
        <h1 className="text-2xl sm:text-3xl font-bold mb-2">{episode.title}</h1>

        {/* Metadata badges */}
        <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-sm text-dark-400 mb-4">
          {episode.duration_ms && (
            <span className="flex items-center gap-1">
              <Clock size={14} />
              {formatDuration(episode.duration_ms)}
            </span>
          )}
          {episode.year && (
            <span className="flex items-center gap-1">
              <Calendar size={14} />
              {episode.year}
            </span>
          )}
          {episode.resolution && (
            <span className="px-2 py-1 bg-dark-700 rounded text-xs">{episode.resolution}</span>
          )}
          {episode.codec && (
            <span className="px-2 py-1 bg-dark-700 rounded text-xs uppercase">{episode.codec}</span>
          )}
          {!isWatched && (
            <span className="px-2 py-1 bg-primary-500/20 text-primary-400 rounded text-xs">New</span>
          )}
        </div>
      </div>

      {/* Thumbnail / Play area */}
      <div
        onClick={handlePlay}
        className="aspect-video bg-dark-800 rounded-xl flex items-center justify-center cursor-pointer group mb-6 relative overflow-hidden"
      >
        {episode.thumbnail_path ? (
          <img
            src={`/api/image/${encodeURIComponent(episode.thumbnail_path)}`}
            alt={episode.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <Tv size={64} className="text-dark-500" />
        )}
        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <div className="w-20 h-20 bg-primary-500 rounded-full flex items-center justify-center transform group-hover:scale-110 transition-transform">
            <Play size={40} className="text-white ml-1" />
          </div>
        </div>
        {isCurrentlyPlaying && (
          <div className="absolute bottom-4 left-4 bg-primary-500 px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2">
            <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
            Now Playing
          </div>
        )}
      </div>

      {/* Play button */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={handlePlay}
          className="btn btn-primary flex items-center gap-2"
        >
          <Play size={20} />
          {isCurrentlyPlaying ? 'Playing' : 'Play Episode'}
        </button>
        <VideoMenu
          videoId={episode.id}
          isWatched={isWatched}
          onWatchedChange={handleWatchedChange}
        />
      </div>

      {/* Overview */}
      {episode.overview ? (
        <div className="card">
          <h2 className="text-lg font-semibold mb-3">Overview</h2>
          <p className="text-dark-300 leading-relaxed">{episode.overview}</p>
        </div>
      ) : (
        <div className="card">
          <p className="text-dark-500 italic">
            No description available. Try refreshing metadata on the series page.
          </p>
        </div>
      )}
    </div>
  )
}
